import { useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useState, useCallback, useRef } from "react";
import { MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

// Lazy load the VibeWidget - only when user clicks
const VibeWidget = lazy(() => import("./VibeWidget"));

export default function ChatWidgetWrapper() {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isPrefetched, setIsPrefetched] = useState(false);
  const notifiedRef = useRef(false);

  // Don't show widget on admin routes or auth pages
  const isAdminOrAuth = pathname.startsWith("/admin") || pathname.startsWith("/auth");

  // Skip render during the build-time prerender pass (Puppeteer headless).
  // The widget adds no SEO value and its markup was inflating every
  // prerendered snapshot's HTML payload.
  const isPrerender =
    typeof navigator !== "undefined" &&
    /HeadlessChrome|Prerender/i.test(navigator.userAgent);

  // Prefetch the widget chunk after page is idle (but don't render it)
  useEffect(() => {
    if (isAdminOrAuth || isPrefetched) return;

    const prefetch = () => {
      // Trigger the lazy import to prefetch the chunk
      import("./VibeWidget").then(() => {
        setIsPrefetched(true);
      }).catch(() => {
        // Silently fail prefetch - will load on click anyway
      });
    };

    // Prefetch after idle or after 4 seconds
    if ('requestIdleCallback' in window) {
      const id = (window as any).requestIdleCallback(prefetch, { timeout: 5000 });
      return () => (window as any).cancelIdleCallback(id);
    } else {
      const timer = setTimeout(prefetch, 4000);
      return () => clearTimeout(timer);
    }
  }, [isAdminOrAuth, isPrefetched]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);

    // Notify admins once per session
    if (!notifiedRef.current) {
      notifiedRef.current = true;
      supabase.functions.invoke('notify-chat-opened', {
        body: { page: pathname },
      }).catch(() => {
        // Silent fail — don't block chat UX
      });
    }
  }, [pathname]);

  if (isAdminOrAuth || isPrerender) {
    return null;
  }

  // If widget is open, show the full widget
  if (isOpen) {
    return (
      <Suspense fallback={
        <div className="fixed bottom-24 right-6 md:bottom-6 z-50">
          <div className="w-14 h-14 rounded-full bg-primary animate-pulse flex items-center justify-center shadow-lg">
            <MessageCircle className="h-6 w-6 text-primary-foreground" />
          </div>
        </div>
      }>
        <VibeWidget />
      </Suspense>
    );
  }

  // Show lightweight button that loads widget on click
  return (
    <button
      onClick={handleOpen}
      className="fixed bottom-24 right-6 md:bottom-6 z-50 w-14 h-14 rounded-full bg-primary hover:bg-primary/90 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2"
      aria-label="Open chat"
    >
      <MessageCircle className="h-6 w-6 text-primary-foreground" />
    </button>
  );
}
